import { observer } from "mobx-react";
import { DefaultModalLayout } from "../../../../components/modal/layouts/default-modal-layout";
import FontHeading2 from "../../../../components/fonts/font-heading2/font-heading2";
import { FONT_COLOR, FONT_VARIANT } from "../../../../components/fonts/font-props";
import { BUTTON_VARIANTS } from "../../../../components/inputs/button-variants";
import FontBody1 from "../../../../components/fonts/font-body1/font-body1";
import TextBanner, { TEXT_BANNER_VARIANTS } from "../text-banner/text-banner";
import FontHeading4 from "../../../../components/fonts/font-heading4/font-heading4";
import ListLayout from "../../../../components/layouts/list-layout/list-layout";
import { Note, NOTE_TYPE } from "../../../../components/note/note";
import AHref from "../../../../components/ahref/ahref";
import './set-up-now-success-modal.scss';
import FontBody2 from "../../../../components/fonts/font-body2/font-body2";
import config from "../../../../config";
import IframeService from "../../../../services/iframe-service/iframe-service";
import shipByReturngoPageStore from "../../ship-by-returngo-page-store";
import useDashboardTranslation from "../../../../dashboard-translation/hooks/dashboard-translation-hook";
import { IButtonProps } from "../../../../components/inputs/button/button";
import useTranslation from "../../../../translation/hooks/translation-hook";
import { rootContainer, TYPE } from "../../../../services/container/root";

const SetUpNowSuccessModal: React.FC = observer(() => {
    const { getTranslation: getDashTransaltion } = useDashboardTranslation();
    const { getTranslation } = useTranslation();
    const returnMethodsLink: string = `${config.apiUrl}/dashboard/settings/return-methods`;
    const billingLink: string = `${config.apiUrl}/dashboard/billing`;
    const walletKbLink: string = `https://support.returngo.ai/wallet`;

    const iframeService = rootContainer.get<IframeService>(TYPE.IframService);

    const onSetUpNowClicked = () => {
        shipByReturngoPageStore.registerMixpanelAdoptionEvent(getTranslation, 'Success', true);
        iframeService.redirectParentFrame(billingLink);
    }

    const onSetUpNowClose = () => {
        shipByReturngoPageStore.registerMixpanelAdoptionEvent(getTranslation, 'Success');
        iframeService.redirectParentFrame(returnMethodsLink);
    }

    const ModalButtons: IButtonProps[] = [
        {
            text: 'Close',
            variant: BUTTON_VARIANTS.Tertiary,
            onClick: onSetUpNowClose,
        }
    ];

    /** If wallet is not yet configured - add Set Up button */
    if (!shipByReturngoPageStore.walletActivated) {
        ModalButtons.push({
            text: 'Set Up Now',
            variant: BUTTON_VARIANTS.PrimaryAccent,
            onClick: onSetUpNowClicked,
        });
    }

    const ResponseMessage: React.FC = () => {
        const failedToConvertReturnMethodNames = shipByReturngoPageStore.failedRoConvert.map(rm => getTranslation(rm.name, 'en'));
        const countSuccessful = shipByReturngoPageStore.convertedReturnMethods.length;
        const countTotal = shipByReturngoPageStore.selectedReturnMethods.length;

        return (
            <>
                <Note
                    type={NOTE_TYPE.INFORMATION}
                    body={
                        <>
                            {countSuccessful} out of {countTotal} selected {' '}
                            <AHref href={returnMethodsLink} target="_blank"><FontBody2 variant={FONT_VARIANT.UNDER_LINE}>return methods</FontBody2></AHref> have
                            been successfully switched to Ship by ReturnGO.
                        </>
                    }
                />

                {shipByReturngoPageStore.failedRoConvert.length > 0 &&
                    <Note
                        type={NOTE_TYPE.ERROR}
                        body={<>Failed to convert: {failedToConvertReturnMethodNames.join(', ')}</>}
                    />
                }
            </>
        );
    };

    return (
        <DefaultModalLayout
            isCloseDisabled={false}
            onCloseClicked={onSetUpNowClose}
            buttons={ModalButtons}
        >
            <div className={'sbr-page-set-up-now-success-modal'}>
                <ListLayout>
                    <ResponseMessage />

                    {!shipByReturngoPageStore.walletActivated &&
                        <div className="set-up-now-success">
                            <FontHeading2 variant={FONT_VARIANT.BOLD}>Activate Your Wallet</FontHeading2>
                            <FontBody1>
                                Your wallet holds a pre-paid balance, from which costs for ReturnGO return labels are automatically deducted. Setting up your wallet is required to use Ship by ReturnGO.
                                <AHref href={walletKbLink} target="_blank">
                                    <FontBody1 variant={FONT_VARIANT.UNDER_LINE}>Learn more</FontBody1>
                                </AHref>
                            </FontBody1>
                        </div>
                    }
                    {shipByReturngoPageStore.walletPromotions &&
                        <>
                            {shipByReturngoPageStore.walletPromotions.map((promotion, index) => (
                                <TextBanner variant={TEXT_BANNER_VARIANTS.GRADIENT} key={index}>
                                    <div className="set-up-now-success-banner-content">
                                        <FontHeading4 color={FONT_COLOR.NEUTRAL_WHITE} variant={FONT_VARIANT.BOLD}>
                                            {getDashTransaltion(promotion.description, 'dashboard-billing')}
                                        </FontHeading4>
                                    </div>
                                </TextBanner>
                            ))}
                        </>
                    }
                </ListLayout>
            </div>
        </DefaultModalLayout>
    );
});

export default SetUpNowSuccessModal;
